import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { t } from '../../i18n';
import { icon } from '../../components/ui/icons';
import { showToast } from '../../components/ui/toast';

interface Grade {
  id: string; grade_number: number; exam_date: string;
  exam_location: string | null; diploma_url: string | null; notes: string | null;
}
interface GradeForm { id?: string; grade_number: string; exam_date: string; exam_location: string; notes: string; }

/** Irish Dance — grade exams (1–12) for one student, with diploma scan upload/download. */
@customElement('cadence-irish-dance-grades')
export class IrishDanceGrades extends LitElement {
  @property() studentId = '';
  @state() private grades: Grade[] = [];
  @state() private loading = true;
  @state() private form: GradeForm | null = null;
  @state() private saving = false;
  @state() private uploadingId = '';
  // A scan chosen in the add/edit modal, uploaded right after the grade is saved
  // (the diploma endpoint needs an existing grade id).
  @state() private diplomaFile: File | null = null;

  static styles = css`
    :host { display: block; }
    .muted { color: var(--color-text-secondary); }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
    .fields { display: flex; flex-direction: column; gap: 12px; }
    .diploma { display: flex; flex-direction: column; gap: 6px; }
    .diploma label { font-size: 0.85rem; font-weight: 500; color: var(--color-text-secondary); }
    .diploma-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .diploma small { font-size: 0.75rem; }

    /* Grade list — rendered in this component's own shadow root (not via cadence-table,
       whose shadow DOM would strip these styles, leaving raw browser buttons) so the
       icon buttons are themed and the rows can reflow to cards on mobile. */
    .glist { border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; }
    .grow { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; border-bottom: 1px solid var(--color-border); }
    .grow:last-child { border-bottom: none; }
    .ginfo { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    .gtitle { font-weight: 600; }
    .gmeta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; font-size: 0.85rem; color: var(--color-text-secondary); }
    .actions { display: flex; gap: 2px; flex-shrink: 0; }
    .iconbtn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 6px; border-radius: 50%; display: inline-flex; transition: background 0.15s, color 0.15s; }
    .iconbtn:hover:not(:disabled) { color: var(--color-primary); background: var(--color-sidebar-hover, rgba(0,0,0,0.06)); }
    .iconbtn.danger:hover:not(:disabled) { color: var(--color-danger, #dc2626); }
    .iconbtn:disabled { opacity: 0.4; cursor: default; }
    a.dl { color: var(--color-primary); text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
    a.dl:hover { text-decoration: underline; }

    /* Pointer/desktop: keep rows clean, reveal actions on hover or keyboard focus. */
    @media (hover: hover) and (min-width: 641px) {
      .actions { opacity: 0; transition: opacity 0.15s; }
      .grow:hover .actions, .grow:focus-within .actions { opacity: 1; }
    }
    /* Mobile (no hover): stack info above a right-aligned action row, card-style. */
    @media (max-width: 640px) {
      .grow { flex-direction: column; align-items: stretch; gap: 8px; }
      .actions { justify-content: flex-end; }
    }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(changed: Map<string, unknown>) { if (changed.has('studentId') && this.studentId) this.load(); }

  private get canEdit() { return store.hasPermission('students.edit'); }

  private async load() {
    if (!this.studentId) return;
    this.loading = true;
    try {
      this.grades = await api.get<Grade[]>(`/modules/irish-dance/grades/${this.studentId}`);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private openNew() {
    if (!this.canEdit) return;
    this.diplomaFile = null;
    this.form = { grade_number: '1', exam_date: '', exam_location: '', notes: '' };
  }
  private openEdit(g: Grade) {
    if (!this.canEdit) return;
    this.diplomaFile = null;
    this.form = { id: g.id, grade_number: String(g.grade_number), exam_date: g.exam_date, exam_location: g.exam_location ?? '', notes: g.notes ?? '' };
  }
  private closeModal() { this.form = null; this.diplomaFile = null; }
  private setField(k: keyof GradeForm, v: string) { if (this.form) this.form = { ...this.form, [k]: v }; }

  /** Choose a scan to attach when saving (not uploaded until the grade exists). */
  private chooseDiplomaFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/jpeg,image/png,image/webp';
    input.onchange = () => { this.diplomaFile = input.files?.[0] ?? null; };
    input.click();
  }

  private async save() {
    if (!this.form) return;
    const f = this.form;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.exam_date)) { showToast(t('irishDance.grades.errDate'), 'error'); return; }
    this.saving = true;
    try {
      const body = { grade_number: Number(f.grade_number), exam_date: f.exam_date, exam_location: f.exam_location.trim(), notes: f.notes.trim() };
      let gradeId = f.id;
      if (f.id) await api.put(`/modules/irish-dance/grades/${f.id}`, body);
      else { const created = await api.post<{ id: string }>(`/modules/irish-dance/grades/${this.studentId}`, body); gradeId = created.id; }
      // Attach the chosen scan to the (now existing) grade; replaces any prior one.
      if (this.diplomaFile && gradeId) await api.upload(`/modules/irish-dance/grades/${gradeId}/diploma`, this.diplomaFile);
      this.closeModal();
      await this.load();
      showToast(t('irishDance.toast.saved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  private async deleteGradeRow(g: Grade) {
    if (!confirm(t('irishDance.grades.confirmDelete', { n: g.grade_number }))) return;
    try {
      await api.del(`/modules/irish-dance/grades/${g.id}`);
      await this.load();
      showToast(t('irishDance.toast.saved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private pickDiploma(g: Grade) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/jpeg,image/png,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      this.uploadingId = g.id;
      try {
        await api.upload(`/modules/irish-dance/grades/${g.id}/diploma`, file);
        await this.load();
        showToast(t('irishDance.grades.diplomaUploaded'), 'success');
      } catch (e) { showToast((e as Error).message, 'error'); }
      finally { this.uploadingId = ''; }
    };
    input.click();
  }

  private async removeDiploma(g: Grade) {
    if (!confirm(t('irishDance.grades.confirmRemoveDiploma'))) return;
    try {
      await api.del(`/modules/irish-dance/grades/${g.id}/diploma`);
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }

  private downloadDiploma(g: Grade) {
    api.download(`/modules/irish-dance/grades/${g.id}/diploma`, `diploma-grade-${g.grade_number}`).catch((e) => showToast((e as Error).message, 'error'));
  }

  render() {
    return html`
      <cadence-card heading=${t('irishDance.grades.heading')}>
        <div class="head">
          <span class="muted">${t('irishDance.grades.lede')}</span>
          ${this.canEdit ? html`<cadence-button @click=${() => this.openNew()}>${icon('plus', 16)} ${t('irishDance.grades.add')}</cadence-button>` : ''}
        </div>
        ${this.loading
          ? html`<p class="muted">${t('common.states.loading')}</p>`
          : this.grades.length === 0
            ? html`<cadence-empty-state heading=${t('irishDance.grades.empty')}></cadence-empty-state>`
            : html`<div class="glist">${this.grades.map((g) => this.renderRow(g))}</div>`}
      </cadence-card>
      ${this.form ? this.renderModal() : ''}
    `;
  }

  private renderRow(g: Grade) {
    return html`
      <div class="grow">
        <div class="ginfo">
          <span class="gtitle">${t('irishDance.grades.gradeN', { n: g.grade_number })}</span>
          <span class="gmeta">
            <span>${g.exam_date}</span>
            ${g.exam_location ? html`<span>· ${g.exam_location}</span>` : ''}
            ${g.diploma_url
              ? html`<a class="dl" href="#" @click=${(e: Event) => { e.preventDefault(); this.downloadDiploma(g); }}>${icon('download', 14)} ${t('irishDance.grades.download')}</a>`
              : ''}
          </span>
        </div>
        ${this.canEdit ? html`
          <div class="actions">
            <button class="iconbtn" title=${t('irishDance.actions.edit')} @click=${() => this.openEdit(g)}>${icon('pencil', 16)}</button>
            <button class="iconbtn" title=${g.diploma_url ? t('irishDance.grades.replaceDiploma') : t('irishDance.grades.uploadDiploma')} ?disabled=${this.uploadingId === g.id} @click=${() => this.pickDiploma(g)}>${icon('upload', 16)}</button>
            ${g.diploma_url ? html`<button class="iconbtn" title=${t('irishDance.grades.removeDiploma')} @click=${() => this.removeDiploma(g)}>${icon('x', 16)}</button>` : ''}
            <button class="iconbtn danger" title=${t('irishDance.actions.delete')} @click=${() => this.deleteGradeRow(g)}>${icon('trash-2', 16)}</button>
          </div>` : ''}
      </div>
    `;
  }

  private renderModal() {
    const f = this.form!;
    const existing = f.id ? this.grades.find((g) => g.id === f.id) : undefined;
    return html`
      <cadence-modal open heading=${f.id ? t('irishDance.grades.editTitle') : t('irishDance.grades.addTitle')} @close=${() => this.closeModal()}>
        <div class="fields">
          <cadence-select label=${t('irishDance.grades.gradeNumber')} .value=${f.grade_number}
            .options=${Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: t('irishDance.grades.gradeN', { n: i + 1 }) }))}
            @change=${(e: CustomEvent) => this.setField('grade_number', e.detail)}></cadence-select>
          <cadence-input label=${t('irishDance.grades.examDate')} type="date" .value=${f.exam_date}
            @input=${(e: CustomEvent) => this.setField('exam_date', e.detail)}></cadence-input>
          <cadence-input label=${t('irishDance.grades.location')} .value=${f.exam_location}
            @input=${(e: CustomEvent) => this.setField('exam_location', e.detail)}></cadence-input>
          <cadence-input label=${t('irishDance.grades.notes')} .value=${f.notes}
            @input=${(e: CustomEvent) => this.setField('notes', e.detail)}></cadence-input>
          <div class="diploma">
            <label>${t('irishDance.grades.colDiploma')}</label>
            <div class="diploma-row">
              <cadence-button variant="secondary" @click=${() => this.chooseDiplomaFile()}>${icon('upload', 16)} ${t('irishDance.grades.chooseFile')}</cadence-button>
              <span class="muted">${this.diplomaFile
                ? this.diplomaFile.name
                : existing?.diploma_url ? t('irishDance.grades.currentAttached') : t('irishDance.grades.noFile')}</span>
            </div>
            <small class="muted">${t('irishDance.grades.fileHint')}</small>
          </div>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => this.closeModal()}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button @click=${() => this.save()} ?disabled=${this.saving}>${t('common.actions.save')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
